import type {
    StorefrontItemBody,
    StorefrontMenuSnapshot,
    StorefrontPlacement,
    StorefrontPrice,
} from '@/api/menu-list/storefront-menu-snapshot.types';
import type { CartMenuItem, CartPackageRef, MenuListsData } from '@/entities/cart/lib/item-price';

export type ResolvedStorefrontItem = {
    hash: string;
    placement: StorefrontPlacement;
    body: StorefrontItemBody;
    /** Effective prices for this placement (override ?? body). */
    prices: StorefrontPrice[];
};

const EMPTY_INDEX: Record<string, ResolvedStorefrontItem> = Object.freeze({});

type SnapshotViews = {
    itemsIndex: Record<string, ResolvedStorefrontItem>;
    cartMenuData: MenuListsData;
};

/** All derived storefront views for one RQ snapshot identity. */
const viewsBySnapshot = new WeakMap<StorefrontMenuSnapshot, SnapshotViews>();

export const resolveStorefrontItem = (
    snapshot: StorefrontMenuSnapshot | null | undefined,
    hash: string | null | undefined,
): ResolvedStorefrontItem | null => {
    if (!snapshot || !hash) return null;
    return getStorefrontItemsIndex(snapshot)[hash] ?? null;
};

const resolvePlacement = (snapshot: StorefrontMenuSnapshot, hash: string): ResolvedStorefrontItem | null => {
    const placement = snapshot.items[hash];
    if (!placement) return null;
    const body = snapshot.bodies[placement.itemId];
    if (!body) return null;
    return {
        hash,
        placement,
        body,
        prices: placement.overridePrices?.length ? placement.overridePrices : body.prices,
    };
};

const buildCartMenuData = (snapshot: StorefrontMenuSnapshot): MenuListsData => {
    const items: MenuListsData['items'] = {};

    for (const [hash, placement] of Object.entries(snapshot.items)) {
        const body = snapshot.bodies[placement.itemId];
        if (!body) continue;

        const packages: CartPackageRef[] = (body.packageItemIds ?? []).map((packageItemId) => {
            const packageItem = snapshot.packageItems[packageItemId];
            return {
                id: packageItemId,
                packageItemId,
                packageItem,
            };
        });

        const cartItem: CartMenuItem = {
            id: body.id,
            name: body.name,
            shortDescription: body.shortDescription,
            description: body.description,
            assets: body.assets,
            prices: body.prices,
            trait: body.trait,
            allergens: body.allergens,
            tags: body.tags,
            packageBehaviours: body.packageBehaviours,
            packages: packages.length ? packages : undefined,
            addons: body.addons,
            variantRows: body.variants?.map((row) => ({
                id: row.id,
                itemId: row.itemId,
                isDefault: row.isDefault,
            })),
            variants: (placement.variantHashes ?? []).map((variantHash) => {
                const variantPlacement = snapshot.items[variantHash];
                return {
                    hash: variantHash,
                    variantItemId: variantPlacement?.variantItemId ?? variantPlacement?.itemId,
                    isDefault: variantPlacement?.isDefault,
                    public: true,
                };
            }),
        };

        items[hash] = {
            type: placement.type,
            isDefault: placement.isDefault,
            parentHash: placement.parentHash,
            hash,
            overrideItem: placement.overridePrices?.length ? { prices: placement.overridePrices } : undefined,
            item: cartItem,
        };
    }

    const itemRefs: Array<{ item: CartMenuItem }> = Object.values(snapshot.packageItems).map((pkg) => ({
        item: {
            id: pkg.id,
            name: pkg.name,
            trait: pkg.trait,
            prices: pkg.prices,
            packageBehaviours: pkg.packageBehaviours,
        },
    }));

    for (const body of Object.values(snapshot.bodies)) {
        if (itemRefs.some((r) => r.item.id === body.id)) continue;
        itemRefs.push({
            item: {
                id: body.id,
                name: body.name,
                trait: body.trait,
                prices: body.prices,
                packageBehaviours: body.packageBehaviours,
            },
        });
    }

    return {
        items,
        menuLists: snapshot.menuLists.map((ml) => ({
            items: ml.categories.flatMap((c) =>
                c.itemHashes.map((hash) => ({ hash, item: items[hash]?.item })),
            ),
            itemRefs,
        })),
    };
};

const getSnapshotViews = (snapshot: StorefrontMenuSnapshot): SnapshotViews => {
    const cached = viewsBySnapshot.get(snapshot);
    if (cached) return cached;

    const itemsIndex: Record<string, ResolvedStorefrontItem> = {};
    for (const hash of Object.keys(snapshot.items)) {
        const resolved = resolvePlacement(snapshot, hash);
        if (resolved) itemsIndex[hash] = resolved;
    }

    const views: SnapshotViews = {
        itemsIndex,
        cartMenuData: buildCartMenuData(snapshot),
    };
    viewsBySnapshot.set(snapshot, views);
    return views;
};

/** Cached resolved index for a snapshot (shared across callers). */
export const getStorefrontItemsIndex = (
    snapshot: StorefrontMenuSnapshot | null | undefined,
): Record<string, ResolvedStorefrontItem> => {
    if (!snapshot) return EMPTY_INDEX;
    return getSnapshotViews(snapshot).itemsIndex;
};

/** Cart-pricing view — cached with the items index on the same snapshot. */
export const getStorefrontCartMenuData = (
    snapshot: StorefrontMenuSnapshot | null | undefined,
): MenuListsData | null => {
    if (!snapshot) return null;
    return getSnapshotViews(snapshot).cartMenuData;
};

/** Flatten public category item hashes (for grids). */
export const listStorefrontCategoryItemHashes = (
    snapshot: StorefrontMenuSnapshot | null | undefined,
): Array<{ hash: string; menuListId: string }> => {
    if (!snapshot) return [];
    const out: Array<{ hash: string; menuListId: string }> = [];

    const walk = (
        categories: StorefrontMenuSnapshot['menuLists'][number]['categories'],
        menuListId: string,
    ) => {
        for (const category of categories) {
            if (!category.public) continue;
            for (const hash of category.itemHashes) {
                out.push({ hash, menuListId });
            }
            walk(category.children, menuListId);
        }
    };

    for (const menuList of snapshot.menuLists) {
        if (!menuList.public) continue;
        walk(menuList.categories, menuList.id);
    }
    return out;
};
